import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import {
  COMMUNICATION_OWNER_TYPES,
  ownerTypeLabel,
  type CommunicationLogPlacementDto,
} from "@/api/communications";
import {
  useAddPlacement,
  useRemovePlacement,
} from "../hooks/useCommunications";
import { PlacementChip } from "./PlacementChip";
import { PlacementOwnerPicker } from "./PlacementOwnerPicker";

interface Props {
  entryId: string;
  placements: CommunicationLogPlacementDto[];
  canEdit: boolean;
}

export function PlacementList({ entryId, placements, canEdit }: Props) {
  const [showAdd, setShowAdd] = useState(false);
  const [ownerType, setOwnerType] = useState<string>("broker");
  const [ownerId, setOwnerId] = useState("");
  // The picker reports back the entity's display name as the user selects;
  // shown inside the picker button until submit so the user can confirm
  // they picked the right record. The DTO refetch supplies the canonical
  // label after the server-side resolver runs.
  const [ownerLabel, setOwnerLabel] = useState<string | undefined>(undefined);
  const add = useAddPlacement(entryId);
  const remove = useRemovePlacement(entryId);
  const { toast } = useToast();

  const submitAdd = async () => {
    if (!ownerId.trim()) return;
    try {
      await add.mutateAsync({ ownerType, ownerId: ownerId.trim() });
      setOwnerId("");
      setOwnerLabel(undefined);
      setShowAdd(false);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.errors?.[0] ?? e.message
          : "Could not add placement.";
      toast({ message: msg, tone: "error" });
    }
  };

  const onRemove = async (p: CommunicationLogPlacementDto) => {
    if (placements.length <= 1) {
      toast({
        message: "Each entry must keep at least one placement.",
        tone: "error",
      });
      return;
    }
    try {
      await remove.mutateAsync({
        ownerType: p.ownerType,
        ownerId: p.ownerId,
      });
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.errors?.[0] ?? e.message
          : "Could not remove placement.";
      toast({ message: msg, tone: "error" });
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-space-2">
        <h2 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0">
          Placements
        </h2>
        {canEdit && !showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-space-1 text-label text-gold-shadow hover:text-gold font-display font-semibold uppercase tracking-label"
          >
            <Plus size={14} aria-hidden /> Add placement
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-space-2">
        {placements.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-space-1">
            <PlacementChip placement={p} />
            {canEdit && (
              <button
                type="button"
                onClick={() => onRemove(p)}
                aria-label={`Remove ${ownerTypeLabel(p.ownerType)} placement`}
                className="text-gray-mid hover:text-status-error"
              >
                <X size={12} aria-hidden />
              </button>
            )}
          </span>
        ))}
      </div>
      {canEdit && showAdd && (
        <div className="mt-space-3 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-space-2">
          <Select
            value={ownerType}
            onChange={(e) => {
              setOwnerType(e.target.value);
              // A previously-picked entity belongs to the prior type's
              // search index; clear so the picker re-opens fresh.
              setOwnerId("");
              setOwnerLabel(undefined);
            }}
            aria-label="Placement type"
          >
            {COMMUNICATION_OWNER_TYPES.map((t) => (
              <option key={t} value={t}>
                {ownerTypeLabel(t)}
              </option>
            ))}
          </Select>
          <PlacementOwnerPicker
            ownerType={ownerType}
            ownerId={ownerId}
            ownerLabel={ownerLabel}
            ariaLabel="Placement record"
            onChange={(id, label) => {
              setOwnerId(id);
              setOwnerLabel(label);
            }}
          />
          <div className="flex gap-space-1">
            <Button
              size="sm"
              type="button"
              onClick={submitAdd}
              loading={add.isPending}
              disabled={!ownerId.trim()}
            >
              Add
            </Button>
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => {
                setShowAdd(false);
                setOwnerId("");
                setOwnerLabel(undefined);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
